import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { ACTIVITIES } from "@/constants/testIds/crm";
import HighlightText from "@/components/data-management/HighlightText";
import SavedViewsBar from "@/components/data-management/SavedViewsBar";
import StatusCountCards from "@/components/data-management/StatusCountCards";
import PaginationBar from "@/components/data-management/PaginationBar";
import { normalizeListResponse } from "@/lib/listResponse";
import { getStatusLabel, getStatusToneClasses } from "@/lib/statusStyles";
const SORT_OPTIONS = [
    { value: "-created_at", label: "Newest first" },
    { value: "created_at", label: "Oldest first" },
    { value: "entity_type", label: "Type A-Z" },
    { value: "actor_name", label: "Actor A-Z" },
];

export default function Activities() {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [filter, setFilter] = useState("");
    const [q, setQ] = useState("");
    const [createdFrom, setCreatedFrom] = useState("");
    const [createdTo, setCreatedTo] = useState("");
    const [sort, setSort] = useState("-created_at");
    const load = useCallback(() => {
        const params = new URLSearchParams({ page, limit: 20 });
        if (filter) params.set("entity_type", filter);
        if (q) params.set("q", q);
        if (createdFrom) params.set("created_from", createdFrom);
        if (createdTo) params.set("created_to", createdTo);
        if (sort) params.set("sort", sort);
        api.get(`/activities?${params.toString()}`).then((r) => {
            const normalized = normalizeListResponse(r.data);
            setItems(normalized.items);
            setCounts(normalized.counts);
            setTotal(normalized.total);
        });
    }, [createdFrom, createdTo, filter, page, q, sort]);
    useEffect(() => { load(); }, [load]);

    const entityTypes = useMemo(() => [
        { v: "", label: "All" },
        { v: "lead", label: "Leads" },
        { v: "follow_up", label: "Follow-ups" },
        { v: "proposal", label: "Proposals" },
        { v: "project", label: "Projects" },
        { v: "client", label: "Clients" },
        { v: "user", label: "Users" },
        { v: "discovery", label: "Discovery" },
    ], []);
    const viewState = { filter, q, createdFrom, createdTo, sort };
    const applyState = (next) => {
        setFilter(next.filter || "");
        setQ(next.q || "");
        setCreatedFrom(next.createdFrom || "");
        setCreatedTo(next.createdTo || "");
        setSort(next.sort || "-created_at");
        setPage(1);
    };
    const typeCards = entityTypes
        .filter((f) => f.v)
        .map((f) => ({ label: f.label, value: counts[f.v] || 0 }));

    return (
        <div className="space-y-6" data-testid={ACTIVITIES.root}>
            <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Audit</div>
                <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Activity Timeline</h1>
                <p className="text-sm text-muted-foreground mt-2">Everything that happens across the platform, chronologically.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search description or actor…" className="md:col-span-2 h-10 px-3 rounded-md border border-input bg-card text-sm" />
                <select value={sort} onChange={(e) => { setPage(1); setSort(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm">
                    {SORT_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
                <input type="date" value={createdFrom} onChange={(e) => { setPage(1); setCreatedFrom(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm" />
                <input type="date" value={createdTo} onChange={(e) => { setPage(1); setCreatedTo(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm" />
            </div>
            <SavedViewsBar
                storageKey="crm.activities.views"
                state={viewState}
                applyState={applyState}
                defaultState={{ filter: "", q: "", createdFrom: "", createdTo: "", sort: "-created_at" }}
            />
            <StatusCountCards
                cards={[
                    { label: "Total Activity", value: total, hint: "All matching records" },
                    ...typeCards,
                ]}
                className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3"
            />
            <div className="flex flex-wrap gap-2">
                {entityTypes.map((f) => (
                    <button key={f.v} onClick={() => { setPage(1); setFilter(f.v); }} className={`h-9 px-4 rounded-full text-xs uppercase tracking-wider border transition-colors ${
                        filter === f.v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"
                    }`}>{f.label}</button>
                ))}
            </div>
            <div className="border border-border rounded-md bg-card p-6">
                {items.length === 0 && <div className="text-sm text-muted-foreground text-center py-10">No activity.</div>}
                <div className="relative pl-6">
                    <div className="absolute left-2 top-0 bottom-0 w-px bg-border" />
                    {items.map((a) => (
                        <div key={a.id} data-testid={ACTIVITIES.row} className={`relative py-4 rounded-md px-3 -mx-3 ${getStatusToneClasses(a.entity_type, { group: "activity", variant: "surface" })}`}>
                            <span className="absolute -left-4 top-5 h-2 w-2 rounded-full bg-primary ring-4 ring-card" />
                            <div className="flex items-start gap-3">
                                <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${getStatusToneClasses(a.entity_type, { group: "activity", variant: "badge" })}`}>{getStatusLabel(a.entity_type)}</span>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm"><span className="font-medium"><HighlightText text={a.actor_name || "System"} query={q} /></span> <span className="text-muted-foreground"><HighlightText text={a.description} query={q} /></span></div>
                                    <div className="text-[11px] text-muted-foreground font-mono mt-0.5">{new Date(a.created_at).toLocaleString()}</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <PaginationBar total={total} page={page} limit={20} onPageChange={setPage} />
            </div>
        </div>
    );
}
